/* eslint-disable no-console */

// Polling that eases off when nobody is there (2026-09-30). A tab left open
// for hours used to keep every setInterval at full speed; now each poll's
// delay follows how long the person has been idle (lib/userActivity.js), the
// timer stops entirely while the tab is hidden, and coming back (or moving
// the mouse after a long idle) brings the fast cadence back — with the next
// call made at once if one is overdue.
//
// Schedules are ordered tiers: the first tier whose `idleUnderMs` exceeds the
// current idle time wins; the last tier (no `idleUnderMs`) is the floor.

import { idleMs, onActivity } from './userActivity.js'

const MIN = 60 * 1000

export const POLL_SCHEDULES = Object.freeze({
  // /link-device, requester side: waiting for an approval given on ANOTHER
  // device, so being at the page is the only hint the person still cares.
  linkRequester: [
    { idleUnderMs: 2 * MIN, everyMs: 3 * 1000 },
    { idleUnderMs: 10 * MIN, everyMs: 10 * 1000 },
    { idleUnderMs: 60 * MIN, everyMs: 30 * 1000 },
    { everyMs: 60 * 1000 },
  ],
  // App-shell watches on every page: incoming key requests, a pending key
  // request of this browser's own, staff support tickets.
  backgroundWatch: [
    { idleUnderMs: 10 * MIN, everyMs: 60 * 1000 },
    { idleUnderMs: 60 * MIN, everyMs: 2 * MIN },
    { everyMs: 5 * MIN },
  ],
  // An open support ticket conversation.
  supportTicket: [
    { idleUnderMs: 10 * MIN, everyMs: 30 * 1000 },
    { idleUnderMs: 60 * MIN, everyMs: 2 * MIN },
    { everyMs: 5 * MIN },
  ],
  // "Processing subscription…" after checkout: the person is watching.
  postPayment: [
    { idleUnderMs: 2 * MIN, everyMs: 1000 },
    { idleUnderMs: 10 * MIN, everyMs: 10 * 1000 },
    { idleUnderMs: 60 * MIN, everyMs: 2 * MIN },
    { everyMs: 5 * MIN },
  ],
})

/** The delay a schedule prescribes for a given idle time. */
export function delayForIdle (schedule, idle) {
  for (const tier of schedule) {
    if (tier.idleUnderMs == null || idle < tier.idleUnderMs) return tier.everyMs
  }
  return schedule[schedule.length - 1].everyMs
}

/**
 * createAdaptivePoll(fn, { schedule, name, pauseWhenHidden, resume })
 *   fn              async work; overlapping runs are never started; errors are logged
 *   schedule        one of POLL_SCHEDULES (or the same shape)
 *   pauseWhenHidden true: no timer while document.hidden
 *   resume          what happens when the tab becomes visible again:
 *                   'due' (default) runs at once only if a call is overdue by the
 *                   current tier; 'now' always runs at once
 * Returns { start({ immediate = true }), stop(), poke(), running }.
 */
export function createAdaptivePoll (fn, { schedule, name = 'poll', pauseWhenHidden = true, resume = 'due' } = {}) {
  if (!Array.isArray(schedule) || !schedule.length) throw new Error('createAdaptivePoll: schedule required')
  const hasDocument = typeof document !== 'undefined'
  let timer = null
  let running = false
  let inFlight = false
  let lastRunAt = 0
  let unsubscribeActivity = null

  const hidden = () => pauseWhenHidden && hasDocument && document.hidden

  function clearTimer () {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  // Recompute the next call from the LAST call and the tier that applies
  // right now — so a long idle stretches the gap, and fresh activity after a
  // long idle shortens it (to "now" if the fast tier's gap has already passed).
  function reschedule () {
    clearTimer()
    if (!running || inFlight || hidden()) return
    const due = lastRunAt + delayForIdle(schedule, idleMs())
    timer = setTimeout(run, Math.max(0, due - Date.now()))
  }

  async function run () {
    if (!running || inFlight) return
    clearTimer()
    inFlight = true
    lastRunAt = Date.now()
    try {
      await fn()
    } catch (e) {
      console.log('[' + name + '] tick failed', e)
    } finally {
      inFlight = false
      reschedule()
    }
  }

  function onVisibilityChange () {
    if (!running) return
    if (document.hidden) {
      clearTimer()
    } else if (resume === 'now') {
      run()
    } else {
      reschedule()
    }
  }

  return {
    start ({ immediate = true } = {}) {
      if (running) return
      running = true
      unsubscribeActivity = onActivity(reschedule)
      if (pauseWhenHidden && hasDocument) document.addEventListener('visibilitychange', onVisibilityChange)
      if (immediate) {
        run()
      } else {
        lastRunAt = Date.now()
        reschedule()
      }
    },
    stop () {
      running = false
      clearTimer()
      if (unsubscribeActivity) {
        unsubscribeActivity()
        unsubscribeActivity = null
      }
      if (pauseWhenHidden && hasDocument) document.removeEventListener('visibilitychange', onVisibilityChange)
    },
    /** Run now (if not already running) and reschedule from there. */
    poke () {
      if (running && !inFlight) run()
    },
    get running () {
      return running
    },
  }
}
